import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Layout, Input, DatePicker, Table, Space, Pagination, Typography, Tag, message, Button, Drawer, Form, Modal, Card } from 'antd';
import { EditOutlined, CheckCircleOutlined, CloseCircleOutlined, PrinterOutlined, FileExcelOutlined, ReloadOutlined, EyeOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import './Registrations.css';
import dayjs from 'dayjs';
import FeedbackPrintModal from './FeedbackPrintModal';
import hash from 'hash-sum';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const StudentFeedbackReport = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTeacher, setSearchTeacher] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState(null);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [viewField, setViewField] = useState(null); // { label, value }
  const [feedbackFormData, setFeedbackFormData] = useState({
    warmup: '',
    task_1: '',
    task_2: '',
    task_3: '',
    homework: '',
    reflection: '',
    comment: ''
    });
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const itemsPerPage = 50;


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString(t('locale'), { day: '2-digit', month: 'short', year: '2-digit' }).toUpperCase();
  };

const fetchFeedbacks = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API_URL}/student-feedback`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setFeedbacks(res.data);
  } catch (err) {
    console.error('❌ Error fetching feedbacks:', err);
  }
}, [API_URL]);


useEffect(() => {
  fetchFeedbacks();
}, [fetchFeedbacks]);

useEffect(() => {
  setCurrentPage(1);
}, [searchTerm, searchTeacher, fromDate, toDate]);

useEffect(() => {
  let filtered = feedbacks;

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(f =>
      f.student_nickname?.toLowerCase().includes(lowerSearch) ||
      f.student_firstname?.toLowerCase().includes(lowerSearch) ||
      f.student_lastname?.toLowerCase().includes(lowerSearch)
    );
  }

  if (searchTeacher) {
    const lowerSearch = searchTeacher.toLowerCase();
    filtered = filtered.filter(f =>
      f.teacher_nickname?.toLowerCase().includes(lowerSearch) ||
      f.teacher_firstname?.toLowerCase().includes(lowerSearch) ||
      f.teacher_lastname?.toLowerCase().includes(lowerSearch)
    );
  }

  if (fromDate) {
    filtered = filtered.filter(f => new Date(f.date) >= fromDate);
  }

  if (toDate) {
    filtered = filtered.filter(f => new Date(f.date) <= toDate);
  }

  setFilteredFeedbacks(filtered);
}, [searchTerm, searchTeacher, fromDate, toDate, feedbacks]);


const handleEdit = async (record) => {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.get(
      `${API_URL}/student-feedback/by-classid-student/${record.classid}/${record.student_id}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const feedback = res.data;
    setSelectedFeedback(feedback);
    setFeedbackFormData({
      warmup: feedback.warmup || '',
      task_1: feedback.task_1 || '',
      task_2: feedback.task_2 || '',
      task_3: feedback.task_3 || '',
      homework: feedback.homework || '',
      reflection: feedback.reflection || '',
      comment: feedback.comment || ''
    });
    setEditDrawerVisible(true);
  } catch (err) {
    console.error('Failed to fetch feedback for editing:', err);
    message.error('Could not load feedback for editing.');
  }
};

const handleSubmitFeedbackUpdate = async () => {
  if (!selectedFeedback?.id) return;

  try {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/student-feedback/${selectedFeedback.id}`, feedbackFormData, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success('Feedback updated successfully');
    
    setEditDrawerVisible(false); // Hide drawer first
    await fetchFeedbacks();      // ✅ Refetch feedbacks after closing
  } catch (error) {
    console.error('Failed to update feedback:', error);
    message.error('Failed to update feedback');
  }
};


const handleApproveToggle = (record) => {
  const isApproved = record.approve === 'approved';
  Modal.confirm({
    title: isApproved ? t('Revoke Approval') : t('Confirm Approval'),
    content: isApproved
      ? t('Are you sure you want to revert this feedback and class to pending?')
      : t('Are you sure you want to approve this feedback and class?'),
    okText: isApproved ? t('Revert') : t('Approve'),
    okType: isApproved ? 'danger' : 'primary',
    cancelText: t('Cancel'),
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${token}` };

        // Toggle feedback approval
        await axios.put(`${API_URL}/student-feedback/approve/${record.id}`, {}, { headers });

        // Approve or unapprove the class
        if (isApproved) {
          await axios.put(`${API_URL}/teacher-hours/${record.classid}/admin-unapprove`, {}, { headers });
        } else {
          await axios.put(`${API_URL}/classes/${record.classid}/adminapprove`, { adminapprove: 'adminconfirm' }, { headers });
        }

        message.success(isApproved ? t('Feedback reverted to pending') : t('Feedback and class approved successfully'));
        await fetchFeedbacks();
      } catch (error) {
        console.error('Error updating approval:', error);
        message.error(t('Failed to update approval'));
      }
    }
  });
};

  const generateFeedbackUID = (feedback) => {
  return hash({
    id: feedback.id,                       
    classid: feedback.classid,
    student_id: feedback.student_id,
    teacher_id: feedback.teacher_id,
    created_at: feedback.created_at,
  });
};

  const currentItems = filteredFeedbacks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const columns = [
    {
    title: t('Class'),
    key: 'class_info',
    ellipsis: true,
    width: 160,
    render: (_, record) => (
      <div>
        <div>{record.class_code}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>ID: {record.classid}</div>
      </div>
    )
  },
  {
    title: t('Subject'),
    dataIndex: 'subject_name',
    key: 'subject_name',
    ellipsis: true
  },
{
  title: t('Date - Time'),
  key: 'date',
  ellipsis: true,
  width: 200,
  render: (record) => {
    const timeStr = record.schedule_time;
    const minutes = Number(record.schedule_hour);
    const date = dayjs(record.date); // ← use `record.date` here

    const [hour, min] = timeStr.split(':');
    const start = new Date(2000, 0, 1, +hour, +min);
    const end = new Date(start.getTime() + minutes * 60000);

    const pad = (n) => n.toString().padStart(2, '0');

    return (
      <div>
        <div>{date.format('DD MMM YY')}</div>
        <div>{`${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>
          {t('Duration')}: {minutes} {t('minutes')}
        </div>
      </div>
    );
  }
},
 {
  title: t('Teacher'),
  key: 'teacher',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.teacher_firstname || ''} ${record.teacher_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.teacher_nickname ? `(${record.teacher_nickname})` : ''}
      </div>
    </div>
  )
},
  {
  title: t('Student'),
  key: 'student',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.student_firstname || ''} ${record.student_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.student_nickname ? `(${record.student_nickname})` : ''}
      </div>
    </div>
  )
},

        {
  title: t('Feedback Content'),
  key: 'feedback_content',
  width: 120,
  render: (_, record) => {
    const fields = [
      { label: t('Warm Up'), value: record.warmup },
      { label: t('Task 1'), value: record.task_1 },
      { label: t('Task 2'), value: record.task_2 },
      { label: t('Task 3'), value: record.task_3 },
      { label: t('Homework'), value: record.homework },
      { label: t('Reflection'), value: record.reflection },
      { label: t('Comment'), value: record.comment },
    ];
    const filled = fields.filter(f => f.value);
    if (!filled.length) return '-';
    const preview = filled[0].value.slice(0, 40) + (filled[0].value.length > 40 ? '…' : '');
    return (
      <div>
        <div style={{ fontSize: '12px', color: '#555', marginBottom: 2 }}>{preview}</div>
        <Button
          type="link"
          size="small"
          danger
          icon={<EyeOutlined />}
          onClick={() => setViewField({ label: t('Feedback Content'), items: fields })}
          style={{ padding: 0 }}
        >
          {t('View')}
        </Button>
      </div>
    );
  }
},
  {
    title: t('updated_by'),
    dataIndex: 'updated_by_name',
    key: 'updated_by_name',
    ellipsis: true,
    width: 150,
     render: (text, record) => (
    <div>
      <div>{text || '-'}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.updated_on ? formatDate(record.updated_on) : ''}
      </div>
    </div>
  )
  },
    {
    title: t('Approval'),
    key: 'approve',
    width: 150,
    render: (_, record) => (
      <div>
        {record.approve === 'approved' ? (
          <Tag color="green">Approved</Tag>
        ) : (
          <Tag color="orange">Pending</Tag>
        )}
        {record.approved_by_name && (
          <div style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>{record.approved_by_name}</div>
        )}
      </div>
    )
  },

{
  title: t('Action'),
  key: 'action',
  fixed: 'right',
  render: (record) => (
    <Space>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={() => handleEdit(record)}
        aria-label="Edit"
      />
      {record.approve === 'approved' ? (
        <Button
          type="link"
          danger
          icon={<CloseCircleOutlined />}
          onClick={() => handleApproveToggle(record)}
        >
        </Button>
      ) : (
        <Button
          type="link"
          icon={<CheckCircleOutlined />}
          style={{ color: 'green' }}
          onClick={() => handleApproveToggle(record)}
        >
        </Button>
      )}
      <Button
        type="link"
        icon={<PrinterOutlined />}
        onClick={() => {
            setPrintRecord(record);
            setPrintModalVisible(true);
        }}
        aria-label="Print"
        />
    </Space>
  )
}

  ];

  const handleDateRangeChange = (dates) => {
    setFromDate(dates ? dates[0].toDate() : null);
    setToDate(dates ? dates[1].toDate() : null);
  };

  return (
    <Layout>
      <Layout>
        <Card style={{ marginBottom: 16 }}>
                    <Title level={3}>
                      <EditOutlined  /> {t('Student Feedback')}</Title>
        <Content>
        

          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder={t('Search by Student Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <Input
              placeholder={t('Search by Teacher Name')}
              value={searchTeacher}
              onChange={(e) => setSearchTeacher(e.target.value)}
              style={{ width: 220 }}
            />
            <RangePicker onChange={handleDateRangeChange} />
            <CSVLink data={currentItems} filename="student_feedback_report.csv">
            <Button icon={<FileExcelOutlined />} type="primary">
                {t('Export CSV')}
            </Button>
            </CSVLink>
            <Button
                icon={<ReloadOutlined />}
                onClick={fetchFeedbacks}
            >
                {t('Refresh')}
            </Button>
          </Space>

          <Table
            columns={columns}
            dataSource={currentItems}
            pagination={false}
            rowKey={(record, index) => index}
            scroll={{ x: 'max-content' }}
            style={{
              overflowX: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          />
          <Pagination
            current={currentPage}
            total={filteredFeedbacks.length}
            pageSize={itemsPerPage}       
            onChange={setCurrentPage}
            style={{ marginTop: 16 }}
            />
        </Content>
        </Card>
      </Layout>
      <Drawer
  title="Edit Feedback"
  open={editDrawerVisible}
  onClose={() => setEditDrawerVisible(false)}
  width={600}
  footer={
    <div style={{ textAlign: 'right' }}>
      <Button onClick={() => setEditDrawerVisible(false)} style={{ marginRight: 8 }}>
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleSubmitFeedbackUpdate}
        disabled={selectedFeedback?.approve !== 'pending'}
      >
        Save
      </Button>
    </div>
  }
>
  {selectedFeedback && (
    selectedFeedback.approve !== 'pending' ? (
      <Tag color="green" style={{ marginBottom: 16 }}>Approved</Tag>
    ) : (
      <Tag color="orange" style={{ marginBottom: 16 }}>Pending Approval</Tag>
    )
  )}

  <Form layout="vertical" disabled={selectedFeedback?.approve !== 'pending'}>
    <Form.Item label="Warmup">
      <Input.TextArea
        value={feedbackFormData.warmup}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, warmup: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 1">
      <Input.TextArea
        value={feedbackFormData.task_1}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_1: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 2">
      <Input.TextArea
        value={feedbackFormData.task_2}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_2: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 3">
      <Input.TextArea
        value={feedbackFormData.task_3}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_3: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Homework">
      <Input.TextArea
        value={feedbackFormData.homework}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, homework: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Reflection">
      <Input.TextArea
        value={feedbackFormData.reflection}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, reflection: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Comment">
      <Input.TextArea
        value={feedbackFormData.comment}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, comment: e.target.value })}
      />
    </Form.Item>
  </Form>
</Drawer>

<Modal
  title={viewField?.label}
  open={!!viewField}
  onCancel={() => setViewField(null)}
  footer={<Button onClick={() => setViewField(null)}>Close</Button>}
  width={600}
>
  {viewField && (
    viewField.items ? (
      viewField.items.filter(i => i.value).map(({ label, value }) => (
        <div key={label} style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
          <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '8px 12px', borderRadius: 4 }}>{value}</div>
        </div>
      ))
    ) : (
      <div style={{ whiteSpace: 'pre-wrap', background: '#f5f5f5', padding: '12px 16px', borderRadius: 4 }}>
        {viewField.value}
      </div>
    )
  )}
</Modal>

{printRecord && (
  <FeedbackPrintModal
    visible={printModalVisible}
    onClose={() => setPrintModalVisible(false)}
    feedback={printRecord}
    classInfo={{
      class_code: printRecord.class_code,
      subject_name: printRecord.subject_name,
      schedule_time: printRecord.schedule_time,
      schedule_hour: printRecord.schedule_hour,
    }}
    teacherInfo={{
      firstname: printRecord.teacher_firstname,
      lastname: printRecord.teacher_lastname,
      nickname: printRecord.teacher_nickname,
    }}
    studentInfo={{
      firstname: printRecord.student_firstname,
      lastname: printRecord.student_lastname,
      nickname: printRecord.student_nickname,
    }}
  />
)}

    </Layout>
    
  );
};

export default withRoleAccess(StudentFeedbackReport, ['admin', 'superadmin']);
